import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { get, post, put, del } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import DeleteModal from "../../components/Custom/DeleteModal/DeleteModal";

const SessionSummaries = () => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalSummaries, setTotalSummaries] = useState(0);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [viewModal, setViewModal] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [taskDetailsModal, setTaskDetailsModal] = useState(false);
  const [therapistNotesModal, setTherapistNotesModal] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Task completion states
  const [taskCompletionModal, setTaskCompletionModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [userResponse, setUserResponse] = useState("");
  const [submittingTask, setSubmittingTask] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    status: "",
    sessionId: "",
    sessionType: "",
    cadenceCompliance: "",
    alertFlag: "",
    startDate: "",
    endDate: "",
  });

  // Function to handle PDF content
  const handlePdfContent = (pdfData) => {
    if (!pdfData) return "No PDF content available";
    
    try {
      // If it's Base64 encoded PDF data
      if (typeof pdfData === 'string' && (pdfData.startsWith('JVBERi') || pdfData.includes('JVBERi'))) {
        // Decode Base64
        const decoded = atob(pdfData);
        
        // For now, return a user-friendly message
        // In a production environment, you'd use a PDF parsing library like pdf.js
        return "PDF document content detected. The summary contains a PDF file with session details. Please contact support for full PDF viewing capabilities.";
      }
      
      // If it's an object with PDF data
      if (typeof pdfData === 'object') {
        if (pdfData.pdfContent) return handlePdfContent(pdfData.pdfContent);
        if (pdfData.base64Data) return handlePdfContent(pdfData.base64Data);
        if (pdfData.pdfText) return pdfData.pdfText;
      }
      
      return "PDF content format not recognized";
    } catch (error) {
      console.error('Error handling PDF content:', error);
      return "Error processing PDF content";
    }
  };

  // Function to format summary data
  const formatSummary = (summaryData) => {
    if (!summaryData) return "No summary available";
    
    // If it's already a string, return as is
    if (typeof summaryData === 'string') {
      // Check if it's Base64 PDF data
      if (summaryData.startsWith('JVBERi') || summaryData.includes('JVBERi')) {
        return handlePdfContent(summaryData);
      }
      
      // Check if it's JSON string and try to parse it
      if (summaryData.trim().startsWith('{') || summaryData.trim().startsWith('[')) {
        try {
          const parsed = JSON.parse(summaryData);
          return formatSummary(parsed);
        } catch (error) {
          // If parsing fails, return as plain text
          return summaryData;
        }
      }
      return summaryData;
    }
    
    // If it's an object/JSON, try to extract meaningful text
    if (typeof summaryData === 'object') {
      try {
        // Handle PDF content
        if (summaryData.pdfContent) return handlePdfContent(summaryData.pdfContent);
        if (summaryData.pdfText) return summaryData.pdfText;
        if (summaryData.base64Data) return handlePdfContent(summaryData.base64Data);
        
        // If it has a 'text' or 'content' property, use that
        if (summaryData.text) return summaryData.text;
        if (summaryData.content) return summaryData.content;
        if (summaryData.summary) return summaryData.summary;
        if (summaryData.description) return summaryData.description;
        
        // If it's an array, join the elements
        if (Array.isArray(summaryData)) {
          return summaryData.map(item => {
            if (typeof item === 'string') return item;
            if (typeof item === 'object' && item.text) return item.text;
            if (typeof item === 'object' && item.content) return item.content;
            return JSON.stringify(item);
          }).join('\n\n');
        }
        
        // If it has multiple properties, format them nicely
        const formattedParts = [];
        for (const [key, value] of Object.entries(summaryData)) {
          if (value && typeof value === 'string') {
            // Skip technical fields
            if (!['_id', 'id', 'createdAt', 'updatedAt', '__v'].includes(key)) {
              formattedParts.push(`${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`);
            }
          }
        }
        
        if (formattedParts.length > 0) {
          return formattedParts.join('\n\n');
        }
        
        // Fallback: return JSON string but formatted nicely
        return JSON.stringify(summaryData, null, 2);
      } catch (error) {
        console.error('Error formatting summary:', error);
        return "Error formatting summary data";
      }
    }
    
    // If it's a number or other type, convert to string
    return String(summaryData);
  };

  const fetchSummaries = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page,
        limit: rowsPerPage,
        search,
        ...filters,
      });

      const response = await get(`/api/dashboard/cbt-session-summaries?${queryParams}`);
      if (response?.success) {
        const summariesWithActions = (response.data.summaries || []).map((summary) => ({
          ...summary,
          onEdit: handleEdit,
          onViewSummary: handleViewSummary,
          onViewTask: handleViewTask,
          onViewNotes: handleViewNotes,
        }));
        setSummaries(summariesWithActions);
        setTotalSummaries(response.data.pagination?.totalSummaries || 0);
      }
    } catch (error) {
      toastMessage("Failed to fetch session summaries", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        startDate: filters.startDate,
        endDate: filters.endDate,
        sessionType: filters.sessionType,
      });

      const response = await get(`/api/dashboard/cbt-session-summaries/analytics?${queryParams}`);
      if (response?.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      toastMessage("Failed to fetch analytics", "error");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummaries();
  }, [page, rowsPerPage, search, filters]);

  useEffect(() => {
    fetchAnalytics();
  }, [filters.startDate, filters.endDate, filters.sessionType]);

  const handleSubmit = async (formData, isEditing, id) => {
    try {
      let response;
      if (isEditing) {
        response = await put(`/api/dashboard/cbt-session-summaries/${id}`, formData);
      } else {
        response = await post("/api/dashboard/cbt-session-summaries", formData);
      }

      if (response?.success) {
        toastMessage(
          isEditing ? "Session summary updated successfully" : "Session summary created successfully",
          "success"
        );
        closeModal();
        fetchSummaries();
        fetchAnalytics();
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || "Something went wrong", "error");
    }
  };

  const handleEdit = (summary) => {
    setEditData(summary);
    setEditModal(true);
    setIsModalOpen(true);
  };

  const handleViewSummary = (summary) => {
    setSelectedSummary(summary);
    setViewModal(true);
  };

  const handleViewTask = (summary) => {
    // Show task details in a separate modal
    setSelectedSummary(summary);
    setTaskDetailsModal(true);
  };

  const handleViewNotes = (summary) => {
    // Show therapist notes in a separate modal
    setSelectedSummary(summary);
    setTherapistNotesModal(true);
  };

  // Task completion functions
  const handleCompleteTask = (task) => {
    setSelectedTask(task);
    setUserResponse("");
    setTaskCompletionModal(true);
  };

  const handleSubmitTaskCompletion = async () => {
    if (!selectedTask || !userResponse.trim()) {
      toastMessage("Please provide your response", "error");
      return;
    }

    try {
      setSubmittingTask(true);
      const response = await put(`/api/app/cbt/tasks/${selectedTask._id}/status`, {
        status: "COMPLETED",
        userResponse: userResponse.trim()
      });

      if (response?.success) {
        toastMessage("Task completed successfully!", "success");
        setTaskCompletionModal(false);
        setSelectedTask(null);
        setUserResponse("");
        fetchSummaries(); // Refresh the summaries
      } else {
        toastMessage(response?.message || "Failed to complete task", "error");
      }
    } catch (error) {
      console.error("Error completing task:", error);
      toastMessage("Failed to complete task", "error");
    } finally {
      setSubmittingTask(false);
    }
  };

  const getTaskStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "IN_PROGRESS":
        return "warning";
      case "OVERDUE":
        return "error";
      case "ASSIGNED":
      default:
        return "default";
    }
  };

  const getTaskStatusIcon = (status) => {
    switch (status) {
      case "COMPLETED":
        return <CheckCircleIcon color="success" />;
      case "IN_PROGRESS":
        return <AssignmentIcon color="warning" />;
      case "OVERDUE":
        return <AssignmentIcon color="error" />;
      case "ASSIGNED":
      default:
        return <AssignmentIcon color="action" />;
    }
  };

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      setIsModalOpen(true);
      setEditModal(false);
      setEditData({});
    } else if (type === "edit") {
      setEditModal(true);
      setIsModalOpen(false);
      setEditData(dataForEdit);
    }
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setEditModal(false);
    }
    setEditData({});
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage + 1);
  };

  const handleSearch = (searchText) => {
    setSearch(searchText);
    setPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed": return "success";
      case "In Progress": return "warning";
      case "Missed": return "error";
      default: return "default";
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h4" gutterBottom>
            CBT Session Summaries & Reports
          </Typography>
          <Typography variant="body2" color="textSecondary">
            View and manage CBT session summaries from existing sessions
          </Typography>
        </Box>

        {/* Analytics Section */}
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssessmentIcon />
              <Typography variant="h6">Analytics & Reports</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {analyticsLoading ? (
              <Typography>Loading analytics...</Typography>
            ) : analytics ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Total Sessions
                      </Typography>
                      <Typography variant="h4">{analytics.totalSessions}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Average Rating
                      </Typography>
                      <Typography variant="h4">
                        {analytics.ratingStats.averageRating.toFixed(1)} ⭐
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Avg Task Completion
                      </Typography>
                      <Typography variant="h4">
                        {analytics.taskStats.averageCompletion.toFixed(1)}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>
                        Compliance Rate
                      </Typography>
                      <Typography variant="h4">
                        {analytics.complianceStats.find(s => s._id === "Compliant")?.count || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Typography>No analytics data available</Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  size="small"
                  placeholder="Search by user name, session ID..."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Missed">Missed</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                    <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Session ID</InputLabel>
                  <Select
                    value={filters.sessionId}
                    onChange={(e) => handleFilterChange("sessionId", e.target.value)}
                    label="Session ID"
                  >
                    <MenuItem value="">All Sessions</MenuItem>
                    {Array.from({ length: 13 }, (_, i) => (
                      <MenuItem key={i} value={`Session ${i}`}>
                        Session {i}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Session Type</InputLabel>
                  <Select
                    value={filters.sessionType}
                    onChange={(e) => handleFilterChange("sessionType", e.target.value)}
                    label="Session Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="DISCOVERY">Discovery</MenuItem>
                    <MenuItem value="ANALYSIS">Analysis</MenuItem>
                    <MenuItem value="SOLUTION">Solution</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Compliance</InputLabel>
                  <Select
                    value={filters.cadenceCompliance}
                    onChange={(e) => handleFilterChange("cadenceCompliance", e.target.value)}
                    label="Compliance"
                  >
                    <MenuItem value="">All Compliance</MenuItem>
                    <MenuItem value="Compliant">Compliant</MenuItem>
                    <MenuItem value="Moderate">Moderate</MenuItem>
                    <MenuItem value="Non-Compliant">Non-Compliant</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Alert Level</InputLabel>
                  <Select
                    value={filters.alertFlag}
                    onChange={(e) => handleFilterChange("alertFlag", e.target.value)}
                    label="Alert Level"
                  >
                    <MenuItem value="">All Alerts</MenuItem>
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange("startDate", e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange("endDate", e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Session Summaries Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Session Summaries
            </Typography>
            
            {/* Search and Filters */}
            <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                label="Search"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                size="small"
                placeholder="Search by user name, session ID..."
                sx={{ minWidth: 200 }}
              />
              <Button
                variant="outlined"
                onClick={() => setFilters({})}
                size="small"
              >
                Clear Filters
              </Button>
            </Box>

            {/* Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User Name</TableCell>
                    <TableCell>Session ID</TableCell>
                    <TableCell>Scheduled Date</TableCell>
                    <TableCell>Actual Attendance</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Summary</TableCell>
                    <TableCell>Task Assigned</TableCell>
                    <TableCell>Task Completion %</TableCell>
                    <TableCell>Attendance Gap</TableCell>
                    <TableCell>Cadence Compliance</TableCell>
                    <TableCell>Summary Validation</TableCell>
                    <TableCell>Task Type</TableCell>
                    <TableCell>Alert/Flag</TableCell>
                    <TableCell>Therapist Notes</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={17} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : summaries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={17} align="center">
                        No session summaries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    summaries.map((summary) => (
                      <TableRow key={summary._id}>
                        <TableCell>
                          {summary.userId?.fullname || "Unknown User"}
                        </TableCell>
                        <TableCell>{summary.sessionId}</TableCell>
                        <TableCell>
                          {new Date(summary.scheduledDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {summary.actualAttendanceDate ? (
                            new Date(summary.actualAttendanceDate).toLocaleString()
                          ) : (
                            <span style={{ color: "gray" }}>Not attended</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={summary.status}
                            color={
                              summary.status === "Completed" ? "success" :
                              summary.status === "In Progress" ? "warning" :
                              summary.status === "Missed" ? "error" : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {summary.duration ? `${summary.duration} minutes` : "–"}
                        </TableCell>
                        <TableCell>
                          {summary.rating ? "⭐".repeat(summary.rating) : "–"}
                        </TableCell>
                        <TableCell>
                          {summary.summary ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewSummary(summary)}
                            >
                              View
                            </Button>
                          ) : (
                            "–"
                          )}
                        </TableCell>
                        <TableCell>
                          {summary.taskAssigned ? (
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {summary.taskAssigned}
                              </Typography>
                              {summary.taskDetails && summary.taskDetails.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  {summary.taskDetails.map((task, index) => (
                                    <Box key={index} sx={{ mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Typography variant="caption" fontWeight="bold">
                                          {task.templateTitle || `Task ${index + 1}`}
                                        </Typography>
                                        <Chip
                                          icon={getTaskStatusIcon(task.status)}
                                          label={task.status?.replace("_", " ") || "ASSIGNED"}
                                          color={getTaskStatusColor(task.status)}
                                          size="small"
                                        />
                                      </Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        {task.templateDescription || "No description"}
                                      </Typography>
                                      {task.status === "ASSIGNED" && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="success"
                                          startIcon={<CheckCircleIcon />}
                                          onClick={() => handleCompleteTask(task)}
                                          sx={{ mr: 1 }}
                                        >
                                          Complete
                                        </Button>
                                      )}
                                      {task.status === "IN_PROGRESS" && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="primary"
                                          onClick={() => handleCompleteTask(task)}
                                          sx={{ mr: 1 }}
                                        >
                                          Continue
                                        </Button>
                                      )}
                                      {task.status === "OVERDUE" && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="error"
                                          onClick={() => handleCompleteTask(task)}
                                          sx={{ mr: 1 }}
                                        >
                                          Complete Now
                                        </Button>
                                      )}
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleViewTask(summary)}
                                      >
                                        View Details
                                      </Button>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No tasks assigned
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {summary.taskCompletionPercentage !== null && summary.taskCompletionPercentage !== undefined ? (
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {summary.taskCompletionPercentage}%
                              </Typography>
                              <Box sx={{ width: '100%', mt: 0.5 }}>
                                <Box
                                  sx={{
                                    width: `${summary.taskCompletionPercentage}%`,
                                    height: 4,
                                    backgroundColor: 
                                      summary.taskCompletionPercentage === 100 ? 'success.main' :
                                      summary.taskCompletionPercentage > 50 ? 'warning.main' :
                                      'error.main',
                                    borderRadius: 2,
                                  }}
                                />
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              –
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {summary.attendanceGap !== null && summary.attendanceGap !== undefined ? (
                            <span style={{ 
                              color: summary.attendanceGap > 0 ? "red" : summary.attendanceGap < 0 ? "green" : "black" 
                            }}>
                              {summary.attendanceGap > 0 ? "+" : ""}{summary.attendanceGap} days
                            </span>
                          ) : (
                            "–"
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={summary.cadenceCompliance}
                            color={
                              summary.cadenceCompliance === "Compliant" ? "success" :
                              summary.cadenceCompliance === "Moderate" ? "warning" :
                              summary.cadenceCompliance === "Non-Compliant" ? "error" : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={summary.summaryValidation}
                            color={
                              summary.summaryValidation === "Complete" ? "success" :
                              summary.summaryValidation === "Pending" ? "warning" :
                              summary.summaryValidation === "Skipped" ? "error" : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{summary.taskType || "–"}</TableCell>
                        <TableCell>
                          {summary.alertFlag && summary.alertFlag !== "None" ? (
                            <Chip
                              label={summary.alertFlag}
                              color={
                                summary.alertFlag === "Critical" ? "error" :
                                summary.alertFlag === "High" ? "warning" :
                                summary.alertFlag === "Medium" ? "info" :
                                summary.alertFlag === "Low" ? "success" : "default"
                              }
                              size="small"
                            />
                          ) : (
                            "–"
                          )}
                        </TableCell>
                        <TableCell>
                          {summary.therapistNotes ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewNotes(summary)}
                            >
                              View
                            </Button>
                          ) : (
                            "–"
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(summary)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="body2" color="textSecondary">
                Showing {((page - 1) * rowsPerPage) + 1} to {Math.min(page * rowsPerPage, totalSummaries)} of {totalSummaries} results
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  size="small"
                >
                  Previous
                </Button>
                <Button
                  disabled={page >= Math.ceil(totalSummaries / rowsPerPage)}
                  onClick={() => setPage(page + 1)}
                  size="small"
                >
                  Next
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Form Modal */}
        <Dialog
          open={isModalOpen || editModal}
          onClose={() => {
            setIsModalOpen(false);
            setEditModal(false);
            setEditData({});
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editModal ? "Edit Session Summary" : "Add Session Summary"}
            <IconButton
              onClick={() => {
                setIsModalOpen(false);
                setEditModal(false);
                setEditData({});
              }}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Note: Session summaries are created automatically from CBT sessions. You can only edit existing summaries.
            </Typography>
            {editModal && editData._id ? (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="User Name"
                    value={editData.userId?.fullname || ""}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Session ID"
                    value={editData.sessionId || ""}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Scheduled Date"
                    value={editData.scheduledDate ? new Date(editData.scheduledDate).toLocaleString() : ""}
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={editData.status || ""}
                      onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="In Progress">In Progress</MenuItem>
                      <MenuItem value="Cancelled">Cancelled</MenuItem>
                      <MenuItem value="Missed">Missed</MenuItem>
                      <MenuItem value="Upcoming">Upcoming</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Rating</InputLabel>
                    <Select
                      value={editData.rating || ""}
                      onChange={(e) => setEditData({ ...editData, rating: e.target.value })}
                      label="Rating"
                    >
                      <MenuItem value={1}>1 ⭐</MenuItem>
                      <MenuItem value={2}>2 ⭐⭐</MenuItem>
                      <MenuItem value={3}>3 ⭐⭐⭐</MenuItem>
                      <MenuItem value={4}>4 ⭐⭐⭐⭐</MenuItem>
                      <MenuItem value={5}>5 ⭐⭐⭐⭐⭐</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Task Completion %"
                    type="number"
                    value={editData.taskCompletionPercentage || ""}
                    onChange={(e) => setEditData({ ...editData, taskCompletionPercentage: parseInt(e.target.value) })}
                    size="small"
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Summary"
                    multiline
                    rows={3}
                    value={editData.summary || ""}
                    onChange={(e) => setEditData({ ...editData, summary: e.target.value })}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Therapist Notes"
                    multiline
                    rows={3}
                    value={editData.therapistNotes || ""}
                    onChange={(e) => setEditData({ ...editData, therapistNotes: e.target.value })}
                    size="small"
                  />
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body1" color="textSecondary" align="center">
                No session selected for editing
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setIsModalOpen(false);
                setEditModal(false);
                setEditData({});
              }}
            >
              Cancel
            </Button>
            {editModal && (
              <Button
                onClick={() => handleSubmit(editData, true, editData._id)}
                variant="contained"
                disabled={!editData._id}
              >
                Update
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* View Modal */}
        <Dialog
          open={viewModal}
          onClose={() => setViewModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Session Summary Details
            <IconButton
              onClick={() => setViewModal(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedSummary && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      User Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.userId?.fullname || "Unknown User"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Session ID
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.sessionId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Scheduled Date
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {new Date(selectedSummary.scheduledDate).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Actual Attendance
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.actualAttendanceDate
                        ? new Date(selectedSummary.actualAttendanceDate).toLocaleString()
                        : "Not attended"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedSummary.status}
                      color={
                        selectedSummary.status === "Completed" ? "success" :
                        selectedSummary.status === "In Progress" ? "warning" :
                        selectedSummary.status === "Missed" ? "error" : "default"
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Duration
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.duration ? `${selectedSummary.duration} minutes` : "–"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Rating
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.rating ? "⭐".repeat(selectedSummary.rating) : "–"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Task Completion
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.taskCompletionPercentage ? `${selectedSummary.taskCompletionPercentage}%` : "–"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Summary
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatSummary(selectedSummary.summary)}
                    </Typography>
                  </Grid>
                  
                  {/* Task Details Section */}
                  {selectedSummary.taskDetails && selectedSummary.taskDetails.length > 0 && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                          Task Details
                        </Typography>
                      </Grid>
                      {selectedSummary.taskDetails.map((task, index) => (
                        <Grid item xs={12} key={index}>
                          <Card variant="outlined" sx={{ p: 2, mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {task.templateTitle}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                              {task.templateDescription}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="textSecondary">
                                  Assigned Tasks
                                </Typography>
                                <Typography variant="body2">
                                  {task.assignedTasks}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="textSecondary">
                                  Completed Tasks
                                </Typography>
                                <Typography variant="body2">
                                  {task.completedTasks}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="textSecondary">
                                  Progress
                                </Typography>
                                <Typography variant="body2">
                                  {task.progress}%
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="textSecondary">
                                  Status
                                </Typography>
                                <Chip
                                  label={task.status}
                                  color={
                                    task.status === "COMPLETED" ? "success" :
                                    task.status === "IN_PROGRESS" ? "warning" :
                                    task.status === "ASSIGNED" ? "info" :
                                    task.status === "OVERDUE" ? "error" : "default"
                                  }
                                  size="small"
                                />
                              </Grid>
                              {task.dueDate && (
                                <Grid item xs={6} md={3}>
                                  <Typography variant="caption" color="textSecondary">
                                    Due Date
                                  </Typography>
                                  <Typography variant="body2">
                                    {new Date(task.dueDate).toLocaleDateString()}
                                  </Typography>
                                </Grid>
                              )}
                              {task.completedAt && (
                                <Grid item xs={6} md={3}>
                                  <Typography variant="caption" color="textSecondary">
                                    Completed At
                                  </Typography>
                                  <Typography variant="body2">
                                    {new Date(task.completedAt).toLocaleDateString()}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Card>
                        </Grid>
                      ))}
                    </>
                  )}
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Therapist Notes
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.therapistNotes || "No notes available"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Task Details Modal */}
        <Dialog
          open={taskDetailsModal}
          onClose={() => setTaskDetailsModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Task Details - {selectedSummary?.sessionId}
            <IconButton
              onClick={() => setTaskDetailsModal(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedSummary && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      User Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.userId?.fullname || "Unknown User"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Session ID
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.sessionId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Total Tasks Assigned
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.taskAssigned}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Overall Completion
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.taskCompletionPercentage}%
                    </Typography>
                  </Grid>
                  
                  {/* Task Details */}
                  {selectedSummary.taskDetails && selectedSummary.taskDetails.length > 0 ? (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                          Assigned Task Templates
                        </Typography>
                      </Grid>
                      {selectedSummary.taskDetails.map((task, index) => (
                        <Grid item xs={12} key={index}>
                          <Card variant="outlined" sx={{ p: 2, mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {task.templateTitle}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                              {task.templateDescription}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="textSecondary">
                                  Assigned Tasks
                                </Typography>
                                <Typography variant="body2">
                                  {task.assignedTasks}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="textSecondary">
                                  Completed Tasks
                                </Typography>
                                <Typography variant="body2">
                                  {task.completedTasks}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="textSecondary">
                                  Progress
                                </Typography>
                                <Typography variant="body2">
                                  {task.progress}%
                                </Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="textSecondary">
                                  Status
                                </Typography>
                                <Chip
                                  label={task.status}
                                  color={
                                    task.status === "COMPLETED" ? "success" :
                                    task.status === "IN_PROGRESS" ? "warning" :
                                    task.status === "ASSIGNED" ? "info" :
                                    task.status === "OVERDUE" ? "error" : "default"
                                  }
                                  size="small"
                                />
                              </Grid>
                              {task.dueDate && (
                                <Grid item xs={6} md={3}>
                                  <Typography variant="caption" color="textSecondary">
                                    Due Date
                                  </Typography>
                                  <Typography variant="body2">
                                    {new Date(task.dueDate).toLocaleDateString()}
                                  </Typography>
                                </Grid>
                              )}
                              {task.completedAt && (
                                <Grid item xs={6} md={3}>
                                  <Typography variant="caption" color="textSecondary">
                                    Completed At
                                  </Typography>
                                  <Typography variant="body2">
                                    {new Date(task.completedAt).toLocaleDateString()}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Card>
                        </Grid>
                      ))}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body1" color="textSecondary" align="center">
                        No tasks assigned for this session
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTaskDetailsModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Therapist Notes Modal */}
        <Dialog
          open={therapistNotesModal}
          onClose={() => setTherapistNotesModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Therapist Notes - {selectedSummary?.sessionId}
            <IconButton
              onClick={() => setTherapistNotesModal(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedSummary && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      User Name
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.userId?.fullname || "Unknown User"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Session ID
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.sessionId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Session Date
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {new Date(selectedSummary.scheduledDate).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Session Status
                    </Typography>
                    <Chip
                      label={selectedSummary.status}
                      color={
                        selectedSummary.status === "Completed" ? "success" :
                        selectedSummary.status === "In Progress" ? "warning" :
                        selectedSummary.status === "Missed" ? "error" : "default"
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Therapist Notes
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedSummary.therapistNotes || "No therapist notes available for this session."}
                    </Typography>
                  </Grid>
                  {selectedSummary.rating && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Session Rating
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedSummary.rating ? "⭐".repeat(selectedSummary.rating) : "–"}
                      </Typography>
                    </Grid>
                  )}
                  {selectedSummary.summary && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Session Summary
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {formatSummary(selectedSummary.summary)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTherapistNotesModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Task Completion Modal */}
        <Dialog 
          open={taskCompletionModal} 
          onClose={() => setTaskCompletionModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Complete Task
            {selectedTask && (
              <Typography variant="body2" color="text.secondary">
                Session {selectedTask.sessionNo} - {selectedTask.sessionType}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            {selectedTask && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedTask.templateTitle || "Untitled Task"}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {selectedTask.templateDescription || "No description available"}
                </Typography>
                
                <Typography variant="subtitle1" gutterBottom>
                  Your Response *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Please describe how you completed this task, what you learned, or any challenges you faced..."
                  value={userResponse}
                  onChange={(e) => setUserResponse(e.target.value)}
                  sx={{ mb: 2 }}
                />
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Tip:</strong> Be specific about what you did, how it made you feel, and what you learned from this task.
                  </Typography>
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setTaskCompletionModal(false)}
              disabled={submittingTask}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitTaskCompletion}
              variant="contained"
              color="success"
              disabled={!userResponse.trim() || submittingTask}
              startIcon={submittingTask ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {submittingTask ? "Completing..." : "Mark as Completed"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default SessionSummaries; 